import React, { useState } from 'react';
import type { Page } from '../../../types';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsPartner } from '../hooks/useIsPartner';
import { cn } from '@/lib/utils';
import { AppIcon, type IconName } from '../../../components/icons/AppIcon';
import {
  LAYOUT_NAV_SECTION_LABEL_CLASS,
  cnLayoutNavIcon,
  cnLayoutNavRow,
} from '../../../components/layout/layoutNavTokens';
import { getSlugFromPage } from '../../../utils/routeUtils';
import { handleSidebarNavClick } from '../../../components/layout/sidebar/navHandlers';
import { SidebarAccountSection } from '../../../components/layout/sidebar/SidebarAccountSection';

type NavItem = { id: Page; label: string; icon: IconName };

const PARTNER_NAV_ITEMS: NavItem[] = [
  { id: 'parceiro', label: 'Dashboard', icon: 'chart' },
];

function NavLink({
  item,
  isActive,
  setCurrentPage,
  onNavigate,
}: {
  item: NavItem;
  isActive: boolean;
  setCurrentPage: (page: Page) => void;
  onNavigate?: () => void;
}) {
  return (
    <a
      href={`/${getSlugFromPage(item.id)}`}
      onClick={(e) => {
        handleSidebarNavClick(e, setCurrentPage, item.id);
        onNavigate?.();
      }}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'group flex min-h-11 w-full items-center gap-s3 rounded-lg px-s3 py-s2 text-sm font-medium touch-manipulation',
        cnLayoutNavRow(isActive),
      )}
    >
      <AppIcon name={item.icon} className={cn('h-4 w-4 shrink-0', cnLayoutNavIcon(isActive))} />
      <span className="min-w-0 truncate">{item.label}</span>
    </a>
  );
}

type PartnerSidebarProps = {
  open?: boolean;
  onClose?: () => void;
};

export function PartnerSidebar({ open = false, onClose }: PartnerSidebarProps) {
  const { currentPage, setCurrentPage } = useAppContext();
  const { user, logout } = useAuth();
  const { partner } = useIsPartner();
  const [avatarError, setAvatarError] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);

  const displayName = partner?.companyName || user?.user_metadata?.full_name || 'Parceiro';
  const initials = displayName.substring(0, 2).toUpperCase();

  return (
    <aside
      id="partner-nav"
      aria-label="Navegação parceiro"
      className={cn(
        'flex w-60 shrink-0 flex-col border-r border-subtle bg-card',
        'pt-s4 md:pt-0',
        open ? 'fixed inset-y-0 left-0 z-50 shadow-lg md:static md:z-auto md:shadow-none' : 'hidden',
        'md:flex',
      )}
    >
      <header className="border-b border-subtle px-s4 py-s4">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">Adlitics</p>
        <p className="mt-s1 text-sm font-semibold text-default">Portal Parceiro</p>
        {partner?.code ? (
          <p className="mt-s2 truncate text-xs text-muted">
            Código:{' '}
            <span className="font-mono font-medium tabular-nums text-secondary">{partner.code}</span>
          </p>
        ) : null}
      </header>

      <nav
        aria-label="Seções do parceiro"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-s4 pt-s3"
      >
        <h2 className={cn(LAYOUT_NAV_SECTION_LABEL_CLASS, 'mb-s2 px-s2')}>PROGRAMA</h2>
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {PARTNER_NAV_ITEMS.map((item) => (
            <li key={item.id}>
              <NavLink
                item={item}
                isActive={currentPage === item.id}
                setCurrentPage={setCurrentPage}
                onNavigate={onClose}
              />
            </li>
          ))}
        </ul>

        {partner?.crmAccessGranted ? (
          <div className="mt-s4">
            <h2 className={cn(LAYOUT_NAV_SECTION_LABEL_CLASS, 'mb-s2 px-2')}>CRM</h2>
            <a
              href={`/${getSlugFromPage('dashboard')}`}
              onClick={(e) => {
                handleSidebarNavClick(e, setCurrentPage, 'dashboard');
                onClose?.();
              }}
              className={cn(
                'group flex min-h-11 w-full items-center gap-s3 rounded-lg px-s3 py-s2 text-sm font-medium touch-manipulation',
                cnLayoutNavRow(false),
              )}
            >
              <AppIcon name="home" className={cn('h-4 w-4 shrink-0', cnLayoutNavIcon(false))} />
              <span className="min-w-0 truncate">Acessar CRM</span>
            </a>
          </div>
        ) : null}
      </nav>

      <footer
        className={cn(
          'mt-auto shrink-0 border-t border-subtle px-s4 pt-s3',
          'pb-[max(0.75rem,env(safe-area-inset-bottom))] md:pb-s3',
        )}
      >
        <SidebarAccountSection
          variant="partner"
          isOpen
          isBlocked={false}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          logout={logout}
          canViewBilling={false}
          canViewSupport={false}
          displayName={displayName}
          userEmail={user?.email}
          userFullName={user?.user_metadata?.full_name}
          initials={initials}
          avatarUrl={user?.user_metadata?.avatar_url}
          avatarError={avatarError}
          setAvatarError={setAvatarError}
          avatarLoaded={avatarLoaded}
          setAvatarLoaded={setAvatarLoaded}
          onNavigate={onClose}
        />
      </footer>
    </aside>
  );
}
